import { FunctionComponent, useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./CheckoutPayment.module.scss";
import { MultiStepProgress } from "../../components/MultiStepProgress";
import { SelectComponent as Select } from "../../components/basic/Select";
import { RadioComponent as Radio } from "../../components/basic/Radio";
import { CompoundSlider as Slider } from "components/basic/Slider";
import { TextFieldComponent as TextField } from "../../components/basic/TextField";
import { CheckboxComponent as Checkbox } from "../../components/basic/Checkbox";
import { checkCardType, cardNumberFormat } from "../../core/helpers";
import amexCard from "../../assets/img/amex_card.png";
import maestroCard from "../../assets/img/maestro_card.png";
import masterCard from "../../assets/img/master_card.png";
import visaCard from "../../assets/img/visa_card.png";
import { Spinner } from "components/basic/Spinner";
import { V12Calculator } from "@/pages/Finance/V12Calculator";
import {
  fetchPaymentReviewDetail,
  selectFormArr,
  selectRegistrationDetail,
  selectFinanceOptions,
  selectFinancePrice,
  submitFinanceOption,
  selectCreditInformation,
  submitCheckoutPayment,
  // selectDekoOption,
  setCardTypeText,
  // setLoanDeposit,
  selectFetchingStatus,
  selectForceDepositMax,
  selectMaximumDeposit,
  clearThreeDSAuthData,
  selectIsSubmitting,
} from "./checkoutPaymentSlice";
import { ButtonRound } from "components/basic/Buttons";
import {
  selectFormValues,
  initializeFormId,
} from "../CheckoutInfo/checkoutInfoSlice";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { PriceBreakdown } from "./PriceBreakdown/PriceBreakdown";
import { Criteria } from "./Criteria/Criteria";
import {
  progressInfosFactory,
  fontKeys,
  logoKeys,
  borderKeys,
  sizeKeys,
} from "../checkout/constants/index";
import { ThreeDAuthDialog } from "@/components/ThreeDAuthDialog/ThreeDAuthDialog";

type Props = {};

const nf = new Intl.NumberFormat("en", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const POUND = "\u00A3";

const appellation = ["Mr", "Miss", "Mrs", "Ms"];
const cardTypeText: { [key: string]: string } = {
  AmexCard: "American Express",
  VisaCard: "Visa",
  MasterCard: "Mastercard",
  MaestroCard: "Maestro",
};

const CURRENT_PAGE_NUMBER = 2;

const CheckoutPayment: FunctionComponent<Props> = (props: Props) => {
  const dispatch = useAppDispatch();

  const { registration }: { registration: string } = useParams() as {
    registration: string;
  };

  const [sliderDomain, setSliderDomain] = useState<[number, number]>([0, 50]);
  const registrationDetail = useAppSelector(selectRegistrationDetail);
  const formArr = useAppSelector(selectFormArr);
  const financeOptions = useAppSelector(selectFinanceOptions);
  const financePrice = useAppSelector(selectFinancePrice);
  const creditInformation = useAppSelector(selectCreditInformation);
  const formValue = useAppSelector(selectFormValues);
  const fetchingStatus = useAppSelector(selectFetchingStatus);
  const maximumDeposit = useAppSelector(selectMaximumDeposit);
  const forceDepositMax = useAppSelector(selectForceDepositMax);
  const isSubmitting = useAppSelector(selectIsSubmitting);
  const formId = formValue?.formId;

  const [payMethod, setPayMethod] = useState("Card");
  const [_depositAmount, setDepositAmount] = useState(25);
  const [flipped, setFlipped] = useState(false);
  const [agreePolicy, setAgreePolicy] = useState(false);
  const [keepUpdate, setKeepUpdate] = useState(false);
  const [dateExpiried, setDateExpired] = useState(false);
  const [cardType, setCardType] = useState("");
  const [cardIcon, setCardIcon] = useState("");
  const [cardBgColor, setCardColor] = useState("");
  const [skipFinanceRecalculation, setSkipFinanceRecalculation] = useState(false);
  const [regPrev, setRegPrev] = useState(
    "https://www.newreg.co.uk/reg.php?reg=" +
      registration +
      "&logo=no_logo&border=border_no"
  );
  const [regPrevFront, setPrevFront] = useState("regular-front");
  const [regPrevBack, setPrevBack] = useState("regular-back");
  const [showFinance, setShowFinance] = useState(false);
  const [cardUseType, setCardUseType] = useState("Credit");

  // finance options
  const [value, setValue] = useState(30);
  const [options, setOption] = useState<Array<string>>([]);
  const [optionKeys, setOptionKeys] = useState<Array<string>>([]);
  const [optionValue, setOptionValue] = useState("");
  const [currentOption, setCurrentOption] = useState<{
    title: string;
    group: string;
    name: string;
    term: string;
    interest: number;
  }>();
  const [selectedFinanceOption, setSelectedFinanceOption] = useState('');

  // payment info
  const [appel, setAppel] = useState("Mr");
  const [firstName, setFirstName] = useState("");
  const [surName, setSurName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");

  // others
  const [thirdPartyQualify, setThirdPartyQualify] = useState(false);
  const [sendCommunciation, setSendCommunciation] = useState(false);
  const [redirectAllowed, setRedirectAllowed] = useState(false);

  const progressInfos = useMemo(
    () => progressInfosFactory({ registration }),
    [registration]
  );
  const progress = useMemo(
    () => progressInfos.map((info) => info.title),
    [progressInfos]
  );

  const navigate = useNavigate();
  const [calculator] = useState(() => new V12Calculator());
  const prevPage = () => {
    // keep query params in the url
    const urlParams = new URLSearchParams(window.location.search);
    const queryString = urlParams.toString();
    const prevPath = progressInfos[CURRENT_PAGE_NUMBER - 1].path;
    
    // if there is query params, append to the path
    navigate(queryString ? `${prevPath}?${queryString}` : prevPath);
  };
  const nextPage = () => {
    sessionStorage?.removeItem("formId");
    sessionStorage?.removeItem("formRegistration");
    sessionStorage?.removeItem("selectedFinanceDetails");
    window.location.href = progressInfos[CURRENT_PAGE_NUMBER + 1].path;
  };

  useEffect(() => {
    dispatch(initializeFormId());
    const urlParams = new URLSearchParams(window.location.search);
    setPayMethod(urlParams.get("finance") === "true" ? "Finance" : "Card");
    
    document.title =
      "New Reg Personalised Number Plates - buy private, cherished and DVLA number plates";
    return () => {
      dispatch(clearThreeDSAuthData());
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (formId) {
      dispatch(
        fetchPaymentReviewDetail({
          registration,
          form_id: formId,
        })
      );
    }
  }, [formId]);

  useEffect(() => {
    if (cardType === "AmexCard") {
      setCardIcon(amexCard);
      setCardColor("#108168");
    }
    if (cardType === "VisaCard") {
      setCardIcon(visaCard);
      setCardColor("#191278");
    }
    if (cardType === "MasterCard") {
      setCardIcon(masterCard);
      setCardColor("#0061A8");
    }
    if (cardType === "MaestroCard") {
      setCardIcon(maestroCard);
      setCardColor("#0B2C5F");
    }
    if (!cardType) {
      setCardIcon("");
      setCardColor("");
    }
  }, [cardType]);

  useEffect(() => {
    let cardTypeMatchText = "";
    if (
      ["MasterCard", "VisaCard"].includes(cardType) &&
      cardUseType === "Debit"
    ) {
      if (cardType === "VisaCard") {
        cardTypeMatchText = "Delta";
      } else {
        cardTypeMatchText = "Mastercard Debit";
      }
    } else {
      cardTypeMatchText = cardTypeText[cardType];
    }
    dispatch(setCardTypeText(cardTypeMatchText));
  }, [cardType, cardUseType]);

  useEffect(() => {
    if (formArr) {
      setAppel(formArr.title || "Mr");
      setFirstName(formArr.forename);
      setSurName(formArr.surname);
      // reg preview
      let regPreview =
        "https://" +
        window.location.host +
        "/reg.php?reg=" +
        registration +
        "&font=" +
        (fontKeys[formArr.digit_style] ? fontKeys[formArr.digit_style] : "") +
        "&border=" +
        borderKeys[formArr.border];
      regPreview = formArr.euro
        ? regPreview + "&logo=" + logoKeys[formArr.euro]
        : regPreview + "&logo=no_logo";
      setRegPrev(regPreview);

      switch (sizeKeys[formArr.plate_size]) {
        case "regular":
          setPrevFront("regular-front");
          setPrevBack("regular-back");
          break;
        case "large":
          setPrevFront("regular-front");
          setPrevBack("large-back");
          break;
        case "square":
          setPrevFront("regular-front");
          setPrevBack("square-back");
          break;
        case "motorbike":
          setPrevFront("motorbike-back");
          setPrevBack("");
          break;
        default:
          setPrevFront("regular-front");
          setPrevBack("regular-back");
          break;
      }

      setShowFinance(
        ["1", "134", "57", "38"].indexOf(formArr.sourcecode) !== -1
      );
    }
  }, [formArr]);

  // calculate slider domain
  const calculateSliderDomain = (key: string): [number, number] => {
    if (!financeOptions[key] || !financePrice) return [0, 50];
    
    const option = financeOptions[key];
    const minLoan = typeof option.minLoan === 'string' ? parseFloat(option.minLoan) : option.minLoan;
    const maxLoan = typeof option.maxLoan === 'string' ? parseFloat(option.maxLoan) : option.maxLoan;
    
    // calculate min deposit percent
    let minDepositPercent = (financePrice - maxLoan) / financePrice * 100;
    minDepositPercent = Math.max(0, minDepositPercent);
    
    // calculate max deposit percent
    let maxDepositPercent = (financePrice - minLoan) / financePrice * 100;
    maxDepositPercent = Math.min(100, maxDepositPercent);
    
    // consider maximumDeposit limit
    maxDepositPercent = Math.min(maxDepositPercent, maximumDeposit);
    
    // ensure minDepositPercent <= maxDepositPercent
    if (minDepositPercent > maxDepositPercent) {
      return [0, maximumDeposit > 0 ? Math.floor(maximumDeposit) : 0];
    }
    
    // since the slider is integer step, we need to convert the percentage value to integer
    // for min value, ceil, ensure Credit Amount not exceed maxLoan
    // for max value, floor, ensure Credit Amount not less than minLoan
    
    // calculate min deposit percent
    const ceiledMinDepositPercent = Math.ceil(minDepositPercent);
    
    // check if the min deposit percent is less than maxLoan
    const depositAmountForCeiledMin = (financePrice / 100) * ceiledMinDepositPercent;
    const creditAmountForCeiledMin = financePrice - depositAmountForCeiledMin;
    
    // if the min deposit percent is less than maxLoan, use the original min deposit percent
    let finalMinDepositPercent = creditAmountForCeiledMin <= maxLoan ? ceiledMinDepositPercent : Math.floor(minDepositPercent);
    
    // calculate max deposit percent
    const flooredMaxDepositPercent = Math.floor(maxDepositPercent);
    
    // check if the max deposit percent is less than minLoan
    const depositAmountForFlooredMax = (financePrice / 100) * flooredMaxDepositPercent;
    const creditAmountForFlooredMax = financePrice - depositAmountForFlooredMax;
    
    // if the max deposit percent is less than minLoan, adjust the max deposit percent
    let finalMaxDepositPercent = flooredMaxDepositPercent;
    if (creditAmountForFlooredMax < minLoan) {
      // recalculate to ensure Credit Amount equals minLoan
      const adjustedMaxDepositPercent = ((financePrice - minLoan) / financePrice) * 100;
      // floor, ensure Credit Amount not less than minLoan
      finalMaxDepositPercent = Math.floor(adjustedMaxDepositPercent);
    }
    
    // ensure max deposit percent not exceed 50% （v12 default max deposit percent）
    finalMaxDepositPercent = Math.min(finalMaxDepositPercent, 50);

    // ensure min value not greater than max value
    if (finalMinDepositPercent > finalMaxDepositPercent) {
      finalMinDepositPercent = finalMaxDepositPercent;
    }
    
    return [finalMinDepositPercent, finalMaxDepositPercent];
  };

  useEffect(() => {
    const arr: Array<string> = [];
    const arr2: Array<string> = [];
    
    // Filter finance products that meet loan amount requirements
    const validFinanceOptions: { [key: string]: any } = {};
    let hasValidOption = false;
    
    for (let key in financeOptions) {
      const option = financeOptions[key];
      const actualLoanAmount = financePrice;
      
      if (actualLoanAmount >= option.minLoan && actualLoanAmount <= option.maxLoan) {
        validFinanceOptions[key] = option;
        arr.unshift(option.name);
        arr2.unshift(key);
        hasValidOption = true;
      }
    }

    if (!hasValidOption && Object.keys(financeOptions).length > 0) {
      // if no valid finance options, hide finance
      setShowFinance(false);
      return;
    }
    
    setOption(arr);
    setOptionKeys(arr2);

    // Check URL parameters to determine if it's a finance path
    const urlParams = new URLSearchParams(window.location.search);
    const isFinancePath = urlParams.get("finance") === "true";

    // Try to get saved finance option from sessionStorage
    const firstKey = arr2[0];
    try {
      const savedFinanceDetails = sessionStorage.getItem('selectedFinanceDetails');
      if (savedFinanceDetails && (payMethod === "Finance" || isFinancePath)) {
        const financeDetails = JSON.parse(savedFinanceDetails);
        setOptionValue(financeDetails.optionKey);
        setValue(financeDetails.depositPercent);
        financeDetails.depositAmount = Math.ceil(parseFloat(financeDetails.depositAmount.toString()) * 100) / 100;
        setDepositAmount(financeDetails.depositAmount);
        
        if (arr.length > 0) {
          const optionIndex = arr2.indexOf(financeDetails.optionKey);
          if (optionIndex !== -1) {
            setSelectedFinanceOption(arr[optionIndex]);
          } else {
            setOptionValue(firstKey);
            setSelectedFinanceOption(arr[0]);
          }
        }

        if (validFinanceOptions[financeDetails.optionKey]) {
          setCurrentOption(validFinanceOptions[financeDetails.optionKey]);
          calculateFinanceFigures(financeDetails.optionKey, financeDetails.depositPercent);
        }
      } else {
        // if there is no saved finance option, use the default value
        setOptionValue(firstKey);
        setSelectedFinanceOption(arr[0]);
        setCurrentOption(validFinanceOptions[firstKey]);
        // calculateFinanceFigures(firstKey, value);
      }
    } catch (error) {
      console.error('Error loading saved finance option:', error);
      // Use default values if there's an error
      setOptionValue(firstKey);
      setSelectedFinanceOption(arr[0]);
      setCurrentOption(validFinanceOptions[firstKey]);
      // calculateFinanceFigures(firstKey, value);
    }
    // calculate and set slider domain
    const domain = calculateSliderDomain(firstKey);
    setSliderDomain(domain as [number, number]);
  }, [financeOptions, financePrice]);

  // add a useEffect to listen to the change of optionValue, update slider domain
  useEffect(() => {
    if (optionValue) {
      const domain = calculateSliderDomain(optionValue);
      setSliderDomain(domain as [number, number]);
    }
  }, [optionValue, financePrice, maximumDeposit]);

  const calculateFinanceFigures = (
    key: string,
    depositPercent: number
  ) => {
    setSkipFinanceRecalculation(true);
    // ensure depositPercent is in the valid range
    const domain = calculateSliderDomain(key);
    const minDepositPercent = domain[0];
    const maxDepositPercent = domain[1];
    
    // since the slider is integer step, ensure depositPercent is integer
    let adjustedDepositPercent = Math.round(depositPercent);
    
    // adjust depositPercent, ensure it is in the valid range
    if (adjustedDepositPercent < minDepositPercent) {
      adjustedDepositPercent = minDepositPercent;
    } else if (adjustedDepositPercent > maxDepositPercent) {
      adjustedDepositPercent = maxDepositPercent;
    }
    
    // if depositPercent exceeds maximumDeposit, use maximumDeposit
    const _despositPercent =
      adjustedDepositPercent > maximumDeposit ? Math.floor(maximumDeposit) : adjustedDepositPercent;
    setValue(_despositPercent);
    let _depositAmount = (financePrice / 100) * _despositPercent;
    
    if (financePrice < 250 && maximumDeposit === 0) {
      _depositAmount = 0;
      setValue(0);
    }

    // ensure Credit Amount not less than minLoan
    const option = financeOptions[key];
    const minLoan = typeof option.minLoan === 'string' ? parseFloat(option.minLoan) : option.minLoan;
    let creditAmount = financePrice - _depositAmount;
    
    if (creditAmount < minLoan) {
      // if Credit Amount is less than minLoan, adjust deposit amount
      _depositAmount = Math.max(0, financePrice - minLoan);
      // recalculate depositPercent, ensure it is integer
      const newDepositPercent = Math.floor((_depositAmount / financePrice) * 100);
      setValue(newDepositPercent);
      // use the new depositPercent to recalculate deposit amount, ensure precise
      _depositAmount = (financePrice / 100) * newDepositPercent;
    }

    _depositAmount = Math.ceil(parseFloat(_depositAmount.toString()) * 100) / 100;
    setDepositAmount(_depositAmount);

    // update sessionStorage selectedFinanceDetails
    const selectedFinanceDetails = {
      optionKey: key,
      depositPercent: _despositPercent,
      depositAmount: _depositAmount
    };
    sessionStorage.setItem('selectedFinanceDetails', JSON.stringify(selectedFinanceDetails));

    console.log('financePrice', financePrice);
    console.log('maximumDeposit', maximumDeposit);
    console.log('_depositAmount', _depositAmount);
    if (!financeOptions[key]) return;

    const selectedProduct = {
      productId: parseInt(financeOptions[key].productId),
      productGuid: financeOptions[key].productGuid,
      name: financeOptions[key].name,
      minLoan: financeOptions[key].minLoan,
      maxLoan: financeOptions[key].maxLoan,
      months: parseInt(financeOptions[key].term),
      monthlyRate: financeOptions[key].monthlyRate,
      apr: financeOptions[key].interest,
      serviceFee: financeOptions[key].serviceFee,
      documentFee: financeOptions[key].documentFee,
      documentFeeCollectionMonth: financeOptions[key].documentFeeCollectionMonth,
      documentFeeMaximum: financeOptions[key].documentFeeMaximum,
      documentFeeMinimum: financeOptions[key].documentFeeMinimum,
      documentFeePercentage: financeOptions[key].documentFeePercentage,
      optionPeriod: financeOptions[key].optionPeriod,
      deferredPeriod: financeOptions[key].deferredPeriod,
      tag: key,
      altTag: financeOptions[key].altTag,
      settlementFee: financeOptions[key].settlementFee,
      calculationFactor: financeOptions[key].calculationFactor
    };

    const result = calculator.calculate(selectedProduct, financePrice, _depositAmount);
    // Format payment_regular_detail based on payment information
    const months = result.months;
    const initialPayments = result.initialPayments ? result.initialPayments : '0.00';
    const finalPayment = result.finalPayment ? result.finalPayment : '0.00';
    let payment_regular_detail = '';
    if (initialPayments === finalPayment) {
      payment_regular_detail = `${months} x ${POUND}${initialPayments}`;
    } else {
      payment_regular_detail = `${months - 1} x ${POUND}${initialPayments} + ${POUND}${finalPayment}`;
    }
    console.log('result', result);
    console.log('payment_regular_detail', payment_regular_detail);
    dispatch(
      submitFinanceOption({
        amount: financePrice,
        product: financeOptions[key].productId,
        deposit: _depositAmount,
        calculationResult: {
          apr: result.apr,
          payment_regular: result.initialPayments,
          payment_regular_detail: payment_regular_detail,
          loan_repayment: (parseFloat(result.balancePayable) + _depositAmount).toFixed(2),
          loan_cost: result.chargeForCredit,
          annual_rate: result.annualRate
        }
      })
    );

    setCurrentOption(financeOptions[key]);
  };

  const cardPaymentSubmit = (e: any) => {
    setRedirectAllowed(true);
    e.preventDefault();
    dispatch(
      submitCheckoutPayment({
        isFinance: false,
        appel,
        firstName,
        surName,
        cardNumber,
        expiry,
        cvv,
        thirdPartyQualify,
        sendCommunciation,
        agreePolicy,
        continueCallback() {
          nextPage();
        },
      })
    );
  };

  const checkDate = (date: string) => {
    const dateStr = date.split("/");
    if (
      parseInt(dateStr[0]) > 12 ||
      parseInt(dateStr[1]) < 2022 ||
      (parseInt(dateStr[1]) === new Date().getFullYear() &&
        parseInt(dateStr[0]) < new Date().getMonth())
    ) {
      setDateExpired(true);
      return false;
    }
    setDateExpired(false);
  };

  useEffect(() => {
    const beforeUnloadEvent = (event: { returnValue: string }) =>
      (event.returnValue = "Leave Site?");
    window.addEventListener("beforeunload", beforeUnloadEvent);
    if (redirectAllowed) {
      window.removeEventListener("beforeunload", beforeUnloadEvent);
    }
    return () => {
      window.removeEventListener("beforeunload", beforeUnloadEvent);
    };
  }, [redirectAllowed]);

  return (
    <div className={styles.CheckoutPayment}>
      <MultiStepProgress stepNames={progress} page={CURRENT_PAGE_NUMBER} />
      <div className={styles.container}>
        <div className="page-title my-4">
          <p className="text-3xl font-bold" style={{ color: "#1678c5" }}>
            You've nearly got your New Reg!
          </p>
          <p className="text-base mt-4" style={{ color: "#868e96" }}>
            You're moments away from standing out from the crowd with your very
            own private personalised registration plate! Simply choose whether
            you'd like to pay now, or pay later with our excellent finance
            options.
          </p>
        </div>
        <div className={`${styles.platePreview} my-4 rounded-md`}>
          <img
            className={styles.plateImg}
            src={regPrev + "&size=" + regPrevFront}
            alt={registration}
          />
          {regPrevBack && (
            <img
              className={styles.plateImg}
              src={regPrev + "&size=" + regPrevBack}
              alt={registration}
            />
          )}
        </div>
        <form onSubmit={cardPaymentSubmit} autoComplete="on">
          <div
            className={`${styles.priceBreakdown} my-4 md:border ${
              fetchingStatus ? "" : "md:p-4"
            } rounded-md relative`}
          >
            {fetchingStatus && <Spinner />}
            <div
              className={`grid xl:grid-cols-2 lg:grid-cols-1 gap-4 ${
                fetchingStatus ? "opacity-10" : ""
              }`}
            >
              <PriceBreakdown
                registration={registration}
                isFinance={payMethod === "Finance"}
              />
              <div className="p-6 bg-card-bg-grey">
                <div className={`${styles.finance}`}>
                  {showFinance &&
                    registrationDetail?.price &&
                    parseFloat(registrationDetail.price) <= 12000 && (
                      <>
                        <p
                          className="text-2xl font-bold"
                          style={{ color: "#1678C5" }}
                        >
                          How would you like to pay?
                        </p>
                        <div className="d-flex justify-around mt-2">
                          <Radio
                            label="Card"
                            checked={payMethod === "Card"}
                            onChange={(e) => setPayMethod("Card")}
                          />
                          <Radio
                            label="Finance"
                            checked={payMethod === "Finance"}
                            onChange={(e) => setPayMethod("Finance")}
                          />
                        </div>
                      </>
                    )}
                  {payMethod === "Card" && (
                    <div className="mt-4">
                      <p className="text-base font-bold mt-4">
                        Pay by debit or credit card:
                      </p>
                      <div className="grid grid-cols-5 gap-4 mt-4">
                        <div className="">
                          <Select
                            label=""
                            onChange={(
                              e: React.FormEvent<HTMLSelectElement>
                            ) => {
                              setAppel(e.currentTarget.value);
                            }}
                            defaultValue={appel}
                            options={appellation}
                          />
                        </div>
                        <div className="col-span-2">
                          <TextField
                            label="First Name"
                            value={firstName}
                            name="forename"
                            errorMsg={
                              !firstName ? "Please enter your firstname." : ""
                            }
                            onChange={(e) =>
                              setFirstName(e.currentTarget.value)
                            }
                            autocomplete="cc-given-name"
                          />
                        </div>
                        <div className="col-span-2">
                          <TextField
                            label="Surname"
                            errorMsg={
                              !surName ? "Please enter your surname." : ""
                            }
                            name="surname"
                            value={surName}
                            onChange={(e) => setSurName(e.currentTarget.value)}
                            autocomplete="cc-family-name"
                          />
                        </div>
                      </div>
                      <div
                        className={`w-full mt-4 grid ${
                          ["MasterCard", "VisaCard"].includes(cardType)
                            ? "grid-cols-4 md:grid-cols-5 gap-4"
                            : "grid-cols-4"
                        }`}
                      >
                        <div className="col-span-4">
                          <TextField
                            label="Card Number:"
                            value={cardNumber}
                            name="cardnumber"
                            icon="credit_card"
                            autocomplete="cc-number"
                            id="cardnumber"
                            errorMsg={
                              cardNumber !== "" && !cardType
                                ? "Please enter correct card number."
                                : ""
                            }
                            onChange={(e) => {
                              const cType = checkCardType(
                                e.currentTarget.value.slice(0, 4)
                              );
                              if (
                                cType === "AmexCard" &&
                                e.currentTarget.value.length > 15
                              )
                                return;
                              if (
                                (cType === "VisaCard" ||
                                  cType === "MasterCard") &&
                                e.currentTarget.value.length > 16
                              )
                                return;
                              if (
                                cType === "MaestroCard" &&
                                e.currentTarget.value.length > 19
                              )
                                return;
                              setCardType(cType);
                              setCardNumber(e.currentTarget.value);
                            }}
                          />
                        </div>
                        {["MasterCard", "VisaCard"].includes(cardType) && (
                          <div className="col-span-4 md:col-span-1">
                            <Select
                              label=""
                              onChange={(
                                e: React.FormEvent<HTMLSelectElement>
                              ) => {
                                setCardUseType(e.currentTarget.value);
                              }}
                              defaultValue={cardUseType}
                              options={["Credit", "Debit"]}
                            />
                          </div>
                        )}
                      </div>
                      <div className={`grid gap-4 mt-4 ${styles.cardExpiryRow}`}>
                        <TextField
                          label="Expiry (MMYYYY)"
                          value={expiry}
                          icon="update"
                          name="cardexpiry"
                          id="cardexpiry"
                          errorMsg={
                            expiry !== "" && dateExpiried
                              ? "Invalid date or date has expired."
                              : ""
                          }
                          autocomplete="cc-exp"
                          onChange={(e) => {
                            if (e.currentTarget.value.length > 7) {
                              return;
                            }
                            let value: string[] | string = e.currentTarget.value
                              .replace("/", "")
                              .split("");
                            if (value.length >= 3) {
                              value.splice(2, 0, "/");
                            }
                            value = value.join("");
                            checkDate(value);
                            if (!/^[0-9]{2}\/[0-9]{4}$/.test(value)) {
                              setDateExpired(true);
                            }
                            setExpiry(value);
                          }}
                        />
                        <TextField
                          label="CVV2"
                          value={cvv}
                          icon="payments"
                          name="signature_strip_number"
                          onChange={(e) => {
                            if (e.currentTarget.value.length > 4) return;
                            if (!/^[0-9]*$/.test(e.currentTarget.value)) return;
                            setCvv(e.currentTarget.value);
                          }}
                          autocomplete="cc-csc"
                          onFocus={() => setFlipped(true)}
                          onBlur={() => setFlipped(false)}
                        />
                      </div>
                      <div
                        className={`${styles.cardPreview} flex justify-center mt-4`}
                      >
                        <div
                          className={`${styles.jpCard} ${
                            flipped ? styles.jpCardFlipped : ""
                          }`}
                        >
                          <div
                            className={`rounded-xl ${
                              cardType ? styles.cardBgImg : ""
                            } ${styles.cardStyle}`}
                            style={{
                              backgroundColor: cardBgColor
                                ? cardBgColor
                                : "#DDD",
                            }}
                          >
                            {!flipped ? (
                              <div
                                className={`${styles.cardFront} w-full h-full`}
                              >
                                <div className={`${styles.cardShiny}`}></div>
                                <div
                                  className="absolute"
                                  style={{
                                    width: 60,
                                    height: 36,
                                    top: 10,
                                    right: 10,
                                  }}
                                >
                                  <img src={cardIcon} alt={cardType} />
                                </div>
                                <div className={`${styles.cardNumber} mt-4`}>
                                  {cardNumber
                                    ? cardNumberFormat(cardNumber)
                                    : "**** **** **** ****"}
                                </div>
                                <div className="md:mt-6 mt-4 flex justify-around">
                                  <div className={`${styles.cardName}`}>
                                    {firstName + " " + surName}
                                  </div>
                                  <div className={`${styles.cardExpiry}`}>
                                    {expiry}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div
                                className={`py-8 w-full h-full ${styles.cardBack}`}
                              >
                                <div className={`${styles.cardBar}`}></div>
                                <div
                                  className={`${styles.cardCvv} md:mt-6 mt-3 relative`}
                                >
                                  <p
                                    className={`md:ml-4 ml-2 text-white w-fit`}
                                    style={{ transform: "rotateY(180deg)" }}
                                  >
                                    {cvv ? cvv : "****"}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="mt-8 text-sm">
                        Please Note: New Reg cannot process your order if the
                        credit/debit card details or address supplied are
                        incorrect. Please make sure that the details correspond
                        with your credit/debit card.
                      </div>
                    </div>
                  )}
                  {payMethod === "Finance" && (
                    <div className="mt-4">
                      <p className="text-base font-bold">
                        Choose deposit amount:
                      </p>
                      {forceDepositMax && maximumDeposit > 0 ? (
                        <p className="text-sm bg-amber-200 p-4 mt-4">
                          Our minimum financeable figure is £250, with this
                          transaction the{" "}
                          <span className="font-bold">
                            maximum allowable deposit is {maximumDeposit}{" "}
                            percent.
                          </span>
                        </p>
                      ) : (
                        <></>
                      )}
                      <div className="my-8">
                        <Slider
                          value={value}
                          domain={sliderDomain}
                          onValueChange={(value: Array<number>) => {
                            calculateFinanceFigures(optionValue, value[0]);
                          }}
                        />
                      </div>
                      <div className="mt-4">
                        <Select
                          label=""
                          onChange={(e: React.FormEvent<HTMLSelectElement>) => {
                            const index = (
                              e.currentTarget as unknown as { index: number }
                            ).index;
                            setOptionValue(optionKeys[index]);
                            setCurrentOption(financeOptions[optionKeys[index]]);
                            setSelectedFinanceOption(options[index]);
                            
                            // update sessionStorage selectedFinanceDetails
                            const selectedFinanceDetails = {
                              optionKey: optionKeys[index],
                              depositPercent: value,
                              depositAmount: _depositAmount
                            };
                            sessionStorage.setItem('selectedFinanceDetails', JSON.stringify(selectedFinanceDetails));
                            
                            calculateFinanceFigures(optionKeys[index], value);
                          }}
                          defaultValue={selectedFinanceOption}
                          options={options}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 md:gap-4">
                        <div>
                          <p className="text-sm flex align-center justify-between border-b py-4 px-1">
                            <span>Cash Price</span>
                            <span className="">
                              {POUND}
                              {nf.format(financePrice)}
                            </span>
                          </p>
                          <p className="text-sm flex align-center justify-between border-b py-4 px-1">
                            <span>Desposit</span>
                            <span className="">
                              {POUND}
                              {_depositAmount.toFixed(2)}
                            </span>
                          </p>
                          <p className="text-sm flex align-center justify-between border-b py-4 px-1">
                            <span>Credit Amount</span>
                            <span className="">
                              {POUND}
                              {nf.format(financePrice - _depositAmount)}
                            </span>
                          </p>
                          <p className="text-sm flex align-center justify-between border-b py-4 px-1">
                            <span>Monthly Payments</span>
                            <span className="">
                              {creditInformation?.payment_regular_detail}
                            </span>
                          </p>
                        </div>
                        <div>
                          <p className="text-sm flex align-center justify-between border-b py-4 px-1">
                            <span>Representative APR</span>
                            <span className="">{creditInformation?.apr}%</span>
                          </p>
                          {creditInformation?.annual_rate  && (
                            <p className="text-sm flex align-center justify-between border-b py-4 px-1">
                              <span>Annual Rate of Interest Fixed</span>
                              <span className="">
                                {creditInformation?.annual_rate}%
                              </span>
                            </p>
                          )}
                          <p className="text-sm flex align-center justify-between border-b py-4 px-1">
                            <span>Total Payable</span>
                            <span className="">
                              {POUND}
                              {creditInformation.loan_repayment}
                            </span>
                          </p>
                        </div>
                      </div>
                      <p className="text-xs mt-4 border-b py-4">
                        * The figures displayed are a representative example. 0%
                        APR Finance available on select plates only. Subject to
                        credit application and approval.
                      </p>
                      <Criteria />
                    </div>
                  )}
                  <p className="font-bold mt-4 text-center">
                    Need help?{" "}
                    <a href="tel:01772 566400" style={{ color: "#2fa1ff" }}>
                      Call us on: 01772 566400
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="grid xl:grid-cols-2 lg:grid-cols-1 gap-8 mt-4 md:border md:p-4 rounded-md">
            <div className="bg-card-bg-grey p-4">
              <p className="text-left md:text-sm text-xs font-semibold">
                Do you authorise the release or qualification of information to
                a third party calling on your behalf? An example could be your
                partner or family member calling to track your order progress.
              </p>
              <div className="d-flex justify-around mt-4">
                <Radio
                  label="Yes"
                  checked={thirdPartyQualify}
                  onChange={(e) => setThirdPartyQualify(true)}
                />
                <Radio
                  label="No"
                  checked={!thirdPartyQualify}
                  onChange={(e) => setThirdPartyQualify(false)}
                />
              </div>
              <i className="text-xs text-slate-500">
                The Data Protection Act is here to ensure that personal
                information is handled fairly and securely.
              </i>
            </div>
            <div className="bg-card-bg-grey p-4">
              <p className="text-left md:text-sm text-xs font-semibold">
                Would you like us to send your communication in plain envelopes?
                Perfect to keep a gift a surprise.
              </p>
              <div className="d-flex justify-around mt-8">
                <Radio
                  label="Yes"
                  checked={sendCommunciation}
                  onChange={(e) => setSendCommunciation(true)}
                />
                <Radio
                  label="No"
                  checked={!sendCommunciation}
                  onChange={(e) => setSendCommunciation(false)}
                />
              </div>
            </div>
          </div>
          <div className="mt-4 md:p-2 md:border rounded-md">
            <div className="flex align-center justify-center p-4 bg-card-bg-grey">
              <Checkbox
                label=""
                checked={agreePolicy}
                onChange={(e: React.FormEvent<HTMLInputElement>) => {
                  setAgreePolicy(!agreePolicy);
                }}
              />
              <span
                className="font-bold cursor-pointer md:text-sm text-xs text-left"
                onClick={() => setAgreePolicy(!agreePolicy)}
              >
                I have read, and agree, to the{" "}
                <a
                  href="/services/terms_and_conditions"
                  style={{ color: "#1e88e5" }}
                  target="_blank"
                >
                  Terms &amp; Conditions
                </a>{" "}
                as well as the{" "}
                <a href="/services/privacy_policy" style={{ color: "#1e88e5" }}>
                  privacy policy
                </a>{" "}
                and wish to proceed.
              </span>
            </div>
          </div>
          <div className="mt-4 md:p-2 md:border rounded-md md:text-sm text-xs">
            <div className="flex align-center justify-center p-4 bg-card-bg-grey">
              <Checkbox
                label=""
                checked={keepUpdate}
                onChange={(e: React.FormEvent<HTMLInputElement>) => {
                  setKeepUpdate(!keepUpdate);
                }}
              />
              <span
                className="font-bold cursor-pointer text-left"
                onClick={() => setKeepUpdate(!keepUpdate)}
              >
                Keep me updated on New Reg news, events and offers.
              </span>
            </div>
          </div>
          <div className="mt-8 mb-8 flex flex-wrap justify-evenly md:grid-cols-2 sm:grid-cols-1 gap-8">
            <ButtonRound
              text="Previous Page"
              type="primary"
              className="previous-page-btn"
              onClick={prevPage}
              style={{ height: 45, width: 320 }}
            />
            {payMethod === "Card" ? (
              <ButtonRound
                text="Submit Payment"
                type="success"
                className="submit-payment-card-btn"
                disabled={
                  !agreePolicy ||
                  !firstName ||
                  !surName ||
                  !cardNumber ||
                  !cardType ||
                  !expiry ||
                  !cvv ||
                  dateExpiried ||
                  isSubmitting
                }
                buttonType="submit"
                style={{ height: 45, width: 320 }}
              />
            ) : (
              <ButtonRound
                text="Continue To Credit Application"
                type="success"
                className="submit-payment-finance-btn"
                disabled={!agreePolicy || isSubmitting}
                style={{ height: 45, width: 320 }}
                onClick={() => {
                  setRedirectAllowed(true);
                  
                  // save current finance details to session storage
                  const selectedFinanceDetails = {
                    optionKey: optionValue,
                    depositPercent: value,
                    depositAmount: _depositAmount
                  };
                  sessionStorage.setItem('selectedFinanceDetails', JSON.stringify(selectedFinanceDetails));
                  
                  dispatch(
                    submitCheckoutPayment({
                      isFinance: true,
                      appel,
                      firstName,
                      surName,
                      cardNumber,
                      expiry,
                      cvv,
                      thirdPartyQualify,
                      sendCommunciation,
                      agreePolicy,
                      // add finance product id and guid
                      finance_product_id: financeOptions[optionValue]?.productId,
                      finance_product_guid: financeOptions[optionValue]?.productGuid,
                      // add deposit amount
                      deposit: _depositAmount,
                      continueCallback() {
                        nextPage();
                      },
                    })
                  );
                }}
              />
            )}
            <ThreeDAuthDialog nextPage={nextPage}></ThreeDAuthDialog>
          </div>
        </form>
      </div>
    </div>
  );
};

export { CheckoutPayment, CheckoutPayment as default };
